import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Average403bBalance.module.css';
import HubNav from '../../HubNav/HubNav';
import PartnersSection from '../../../NewUI/Home/components/PartnersSection';

const Average403bBalance = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className={styles.container}>
      <HubNav />

      <div className={styles.hero}>
        <div className={styles['hero-inner']}>
          <div className={styles['hero-eyebrow']}>📊 Research · Plootus 2026 · Teachers, Healthcare, Nonprofits</div>
          <h1>Average 403(b) Balance by Age (2026): Teacher, Nurse & Nonprofit Worker Benchmarks</h1>
          <div className={styles['hero-deck']}>
            Most 403(b) participants are teachers, healthcare workers, and nonprofit employees — groups with specific salary trajectories and retirement planning needs that differ from typical corporate 401(k) holders. Here's where you actually stand.
          </div>
          <div className={styles['hero-meta']}>
            <span>By the Plootus Research Team</span>
            <span>Updated April 2026</span>
            <span>Sources: TIAA Institute, Vanguard 2025, Federal Reserve SCF 2022, IRS 2026</span>
          </div>
        </div>
      </div>

      <div className={styles['stat-strip']}>
        <div className={styles['stat-strip-inner']}>
          <div className={styles['stat-item']}>
            <div className={styles['stat-num']} data-type="statistic">~$108K</div>
            <div className={styles['stat-label']}>Avg 403(b) Balance Ages 45-54</div>
          </div>
          <div className={styles['stat-item']}>
            <div className={styles['stat-num']} data-type="statistic">~$242K</div>
            <div className={styles['stat-label']}>Avg 403(b) Balance Ages 55-64</div>
          </div>
          <div className={styles['stat-item']}>
            <div className={styles['stat-num']} data-type="statistic">$24,500</div>
            <div className={styles['stat-label']}>2026 403(b) Contribution Limit</div>
          </div>
          <div className={styles['stat-item']}>
            <div className={styles['stat-num']} data-type="statistic">$85K</div>
            <div className={styles['stat-label']}>Lost to 1.5% Annuity Fee Over 20 Yrs</div>
          </div>
        </div>
      </div>

      <div className={styles['page-body']} itemScope itemType="https://schema.org/Article">
        <main className={styles['main-content']} role="main" aria-label="Average 403b Balance by Age 2026 — Teacher and Nonprofit Benchmarks">
          <section id="data">
            <div className={styles['section-label']}>Data</div>
            <h2>403(b) Balance Benchmarks by Age (2026 Estimates)</h2>
            <div className={styles['warn-box']}>
              <p>⚠️ <strong>Data limitation note:</strong> Unlike 401(k) plans, comprehensive federal data on 403(b) balances separately from other defined contribution plans is limited. These benchmarks are derived from TIAA Institute research (covering higher education and nonprofit employees), Federal Reserve SCF 2022 (all DC plan participants in public/nonprofit sectors), and Plootus analysis. Actual balances vary significantly based on employer match availability, pension supplements, and plan fee structures.</p>
            </div>

            <table className={styles['data-table']} summary="Average 403b balance by age with Fidelity salary benchmarks and 2026 contribution limits">
              <thead>
                <tr>
                  <th scope="col">Age Group</th>
                  <th scope="col">Avg. 403(b)<br />Balance (Est.)</th>
                  <th scope="col">Fidelity Salary<br />Benchmark</th>
                  <th scope="col">2026 Contrib.<br />Limit</th>
                  <th scope="col">Unique 15-Yr<br />Catch-Up</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Under 30</strong></td>
                  <td data-type="statistic">$8,000–$18,000</td>
                  <td>1× salary</td>
                  <td>$24,500</td>
                  <td>Not yet eligible</td>
                </tr>
                <tr>
                  <td><strong>30–39</strong></td>
                  <td data-type="statistic">$28,000–$52,000</td>
                  <td>2–3× salary</td>
                  <td>$24,500</td>
                  <td>Check eligibility</td>
                </tr>
                <tr>
                  <td><strong>40–44</strong></td>
                  <td data-type="statistic">$58,000–$92,000</td>
                  <td>3× salary</td>
                  <td>$24,500</td>
                  <td>If 15+ yrs at employer</td>
                </tr>
                <tr>
                  <td><strong>45–54</strong></td>
                  <td data-type="statistic">$108,000–$152,000</td>
                  <td>6× salary</td>
                  <td>$24,500</td>
                  <td>If eligible: +$3,000/yr</td>
                </tr>
                <tr>
                  <td><strong>55–59</strong></td>
                  <td data-type="statistic">$152,000–$210,000</td>
                  <td>7–9× salary</td>
                  <td data-type="statistic" className={styles.hi}>$32,500 (age 50+)</td>
                  <td>If eligible: +$3,000/yr</td>
                </tr>
                <tr>
                  <td><strong>60–63</strong></td>
                  <td data-type="statistic">$187,000–$242,000</td>
                  <td>8–9× salary</td>
                  <td data-type="statistic" className={styles.hi}><strong>$35,750 (SECURE 2.0)</strong></td>
                  <td>If eligible: +$3,000/yr (cannot combine with super catch-up)</td>
                </tr>
                <tr>
                  <td><strong>64+</strong></td>
                  <td data-type="statistic">$210,000–$280,000</td>
                  <td>10× salary</td>
                  <td>$32,500</td>
                  <td>N/A</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section id="fee-warning">
            <div className={styles['section-label']}>Critical Issue</div>
            <h2>The 403(b) Fee Problem — How Much It's Costing You</h2>
            <p>An estimated 60% of 403(b) assets are held in high-cost annuity products from insurance companies, carrying annual fees of 0.5–3% or more. This is the single biggest financial planning issue for most 403(b) participants.</p>
            <table className={styles['data-table']} summary="403b fee impact comparison showing cost of annuity fees vs index funds">
              <thead>
                <tr>
                  <th scope="col">Expense<br />Ratio</th>
                  <th scope="col">$100K Over 20<br />Years at 7%</th>
                  <th scope="col">Net<br />Balance</th>
                  <th scope="col">vs. 0.05%<br />Index Fund</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles.green}><strong>0.05% (Low-cost index fund)</strong></td>
                  <td>7% effective return</td>
                  <td data-type="statistic" className={styles.green}><strong>$386,000</strong></td>
                  <td>Baseline</td>
                </tr>
                <tr>
                  <td>0.50% (Better annuity products)</td>
                  <td>6.5% effective</td>
                  <td data-type="statistic">$352,000</td>
                  <td className={styles.bad}>−$34,000</td>
                </tr>
                <tr>
                  <td>1.50% (Typical annuity)</td>
                  <td>5.5% effective</td>
                  <td data-type="statistic">$292,000</td>
                  <td className={styles.bad}>−$94,000</td>
                </tr>
                <tr>
                  <td className={styles.bad}>2.50% (High-cost annuity)</td>
                  <td>4.5% effective</td>
                  <td data-type="statistic" className={styles.bad}>$241,000</td>
                  <td className={styles.bad}>−$145,000</td>
                </tr>
              </tbody>
            </table>
            <div className={styles['info-box']}>
              <p>💡 <strong>What to do:</strong> Request your plan's fund menu and look for expense ratios. Ask your HR department or plan administrator if lower-cost options are available. In many plans, Vanguard, Fidelity, or TIAA mutual fund options are available alongside the default annuity products. Plootus analyzes your 403(b) fund options and recommends the lowest-cost alternatives — free.</p>
            </div>
          </section>

          <section id="faq" aria-label="Frequently Asked Questions" className={styles['faq-section']}>
            <h2>Frequently Asked Questions</h2>
            {[
              {
                q: 'How much should I have in my 403(b) at 55?',
                a: 'Fidelity\'s benchmark of 7× your annual salary at age 55 is a useful starting target. On a $58,000 salary (approximate median for K-12 teachers in the U.S.), that\'s a target of $406,000. Many 403(b) participants at 55 fall below this because of lower employer matches, higher plan fees, and lower salaries in nonprofit sectors. If you\'re behind, the catch-up contribution rules become critically important: at age 50+, you can contribute $32,500/year; at 60-63 under SECURE 2.0, you can contribute $35,750/year.',
              },
              {
                q: 'Should teachers rely only on their pension or also save in a 403(b)?',
                a: 'Pensions provide guaranteed income but typically replace only 50-70% of pre-retirement salary. Healthcare costs in retirement (potentially $400,000+ for a couple), inflation risk, and the desire for financial flexibility all argue for supplementing a pension with 403(b) savings. Additionally, many teachers who change states or districts may not vest in their pension — a 403(b) is portable. At minimum, contribute enough to get any employer match, then aim to build a 403(b) balance that can cover healthcare costs and supplement pension income.',
              },
            ].map((faq, index) => (
              <div
                key={index}
                className={`${styles['faq-item']} ${openFaq === index ? styles['faq-item-open'] : ''}`}
              >
                <div className={styles['faq-q']} onClick={() => toggleFaq(index)}>
                  {faq.q}
                  <span>+</span>
                </div>
                {openFaq === index && (
                  <div className={styles['faq-a']} style={{ display: 'block' }}>
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </section>

          <div className={styles['sources-box']}>
            <strong>📚 Sources</strong>
            <ul>
              <li>TIAA Institute, Retirement Plan Research and Financial Wellness Data 2025</li>
              <li>Federal Reserve, Survey of Consumer Finances 2022 — public/nonprofit sector participants</li>
              <li>IRS, 403(b) Contribution Limits 2026; 15-Year Service Rule</li>
              <li>Plootus Research 2026 — 403(b) fee analysis</li>
              <li>SECURE 2.0 Act (Consolidated Appropriations Act, 2023)</li>
            </ul>
          </div>
        </main>

        <aside className={styles.sidebar} role="complementary" aria-label="Related resources and navigation">
          <div className={styles['toc-box']}>
            <h3>On This Page</h3>
            <ul>
              <li><a href="#data">Balance Data</a></li>
              <li><a href="#fee-warning">Fee Problem</a></li>
              <li><a href="#faq">FAQs</a></li>
            </ul>
          </div>
          <div className={styles['cta-card']}>
            <h4>What's my retirement number?</h4>
            <p>Using our Retirement Calculator, let us help you figure out if you are on track for your planned retirement.</p>
            <a onClick={() => navigate('/retirement-calculator')} className={styles['cta-btn']}>
              Check Here
            </a>
          </div>
          <div className={`${styles['sidebar-card']} ${styles['related-card']}`}>
            <h4>Related Guides</h4>
            <a href="/403b-guide">→ 403(b) Plan Guide</a>
            <a href="/average-ira-balance-by-age">→ Average IRA Balance by Age</a>
            <a href="/compound-interest-calculator">→ Compound Interest Calculator</a>
            <a href="/how-to-plan-retirement">→ How to Plan for Retirement</a>
          </div>
          <div className={styles['sidebar-card']} style={{ background: 'var(--off-white)' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-light)', marginBottom: '8px' }}>
              Key Stat
            </p>
            <p style={{ fontSize: '15px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1.45, marginBottom: '5px' }}>
              "A 1.5% annual 403(b) fee costs approximately $94,000 more than a 0.05% index fund over 20 years on a $100,000 balance."
            </p>
            <p style={{ fontSize: '11px', color: 'var(--text-light)', margin: 0 }}>
              Source: Plootus Research 2026
            </p>
          </div>
        </aside>
      </div>
      <PartnersSection 
        titleFontSize="22px"
        titleFontWeight={800}
        titleColor="var(--navy) !important"
        titleLetterSpacing="-0.3px"
        subtitleFontSize="15px"
        subtitleColor="var(--text-mid)"
        rootPadding="40px 0 0"
      />
    </div>
  );
};

export default Average403bBalance;
